import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { dashboardApi, authApi, usersApi } from "../lib/api";
import { UserAvatar } from "./UserAvatar";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { LOGO_DARK_BG } from "../lib/branding";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard, FolderKanban, Clock, CheckSquare, FileBarChart,
  Users, ListTodo, Menu, X, LogOut, User, CalendarDays, ShoppingCart,
  FileText, Receipt, Bell, AlertTriangle, ChevronRight, ChevronDown, Bot,
  Activity, KeyRound, Package, Camera,
} from "lucide-react";

const LOGO_URL = LOGO_DARK_BG;
// Use the clean PNG for display (white bg + screen blend = transparent)
const LOGO_DISPLAY = "https://customer-assets.emergentagent.com/job_bc07a5b0-4401-454c-9821-20d81dadd6da/artifacts/21c8lw1c_fa793a21-0229-4214-a16b-e1c0df8a86d3.png";

const Layout = ({ children }) => {
  const { user, logout, canManage, isAdmin, updateUser } = useAuth();

  const handleAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Bilden får max vara 5 MB"); return; }
    try {
      const res = await usersApi.uploadAvatar(file);
      updateUser({ profile_image: res.data.profile_image });
      toast.success("Profilbild uppdaterad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte ladda upp bilden");
    }
  };
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", new1: "", new2: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.new1) return;
    if (pwForm.new1 !== pwForm.new2) {
      toast.error("Losenorden matchar inte");
      return;
    }
    setPwLoading(true);
    try {
      await authApi.changePassword(pwForm.current, pwForm.new1);
      toast.success("Losenord andrat!");
      setPwOpen(false);
      setPwForm({ current: "", new1: "", new2: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte andra losenord");
    } finally { setPwLoading(false); }
  };

  const navGroups = [
    {
      label: null,
      items: [
        { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["all"] },
        { path: "/ai", label: "AI Assistent", icon: Bot, roles: ["all"] },
      ],
    },
    {
      label: "Arbete",
      items: [
        { path: "/time-entry", label: "Tidsrapportering", icon: Clock, roles: ["all"] },
        { path: "/schedule", label: "Planering", icon: CalendarDays, roles: ["all"] },
        { path: "/projects", label: "Projekt", icon: FolderKanban, roles: ["all"] },
      ],
    },
    {
      label: "Material",
      items: [
        { path: "/shopping", label: "Inköpslista", icon: ShoppingCart, roles: ["all"] },
        { path: "/inventory", label: "Förråd", icon: Package, roles: ["all"] },
        { path: "/documents", label: "Dokument", icon: FileText, roles: ["all"] },
      ],
    },
    {
      label: "Ekonomi",
      items: [
        { path: "/profitability", label: "Lonsamhet", icon: Activity, roles: ["admin", "projektledare"] },
        { path: "/invoices", label: "Fakturor", icon: Receipt, roles: ["admin", "projektledare"] },
        { path: "/approvals", label: "Godkannanden", icon: CheckSquare, roles: ["admin", "projektledare"] },
        { path: "/reports", label: "Rapporter", icon: FileBarChart, roles: ["admin", "projektledare"] },
      ],
    },
    {
      label: "System",
      items: [
        { path: "/activities", label: "Aktiviteter", icon: ListTodo, roles: ["admin", "projektledare"] },
        { path: "/employees", label: "Anställda", icon: Users, roles: ["all"] },
      ],
    },
  ];

  const filteredGroups = navGroups.map(g => ({
    ...g,
    items: g.items.filter(i => i.roles.includes("all") || i.roles.includes(user?.role)),
  })).filter(g => g.items.length > 0);

  const loadNotifications = useCallback(async () => {
    if (!canManage) return;
    try {
      const [statsRes, missingRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getMissingReports().catch(() => ({ data: { missing_today: [], low_week_hours: [] } })),
      ]);
      const notifs = [];
      const pending = statsRes.data.pending_approvals;
      if (pending > 0) {
        notifs.push({ id: "pending", text: `${pending} tidsposter att godkänna`, icon: CheckSquare, color: "text-amber-400", path: "/approvals" });
      }
      const missing = missingRes.data.missing_today?.length || 0;
      if (missing > 0) {
        notifs.push({ id: "missing", text: `${missing} har ej rapporterat idag`, icon: AlertTriangle, color: "text-red-400", path: "/dashboard" });
      }
      setNotifications(notifs);
    } catch { /* ignore */ }
  }, [canManage]);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  const getRoleLabel = (role) => ({ admin: "Admin", projektledare: "Projektledare", "anställd": "Anställd", "underentreprenör": "UE" }[role] || role);

  const NavLink = ({ item, onClick }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    return (
      <Link to={item.path} onClick={onClick} data-testid={`nav-${item.path.slice(1)}`}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all duration-200 ${
          isActive 
            ? "bg-[#6dbf4a]/15 text-[#6dbf4a] font-semibold shadow-lg shadow-[#6dbf4a]/5"
            : "text-white/40 hover:text-white/80 hover:bg-white/[0.04]"
        }`}>
        <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? "drop-shadow-[0_0_6px_rgba(109,191,74,0.4)]" : ""}`} />
        <span>{item.label}</span>
      </Link>
    );
  };

  const SidebarContent = ({ onLinkClick }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-20 flex items-center justify-center px-4">
        <img src={LOGO_DISPLAY} alt="Mälair" className="h-16 w-auto" style={{ mixBlendMode: 'screen', filter: 'brightness(1.2)' }} />
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto space-y-5">
        {filteredGroups.map((group, gi) => (
          <div key={gi}>
            {group.label && (
              <p className="text-[10px] tracking-[0.12em] uppercase font-semibold text-white/20 px-3 mb-2">{group.label}</p>
            )}
            <div className="space-y-1">
              {group.items.map(item => <NavLink key={item.path} item={item} onClick={onLinkClick} />)}
            </div>
          </div>
        ))}
      </nav>

      {/* User */}
      <input id="avatar-file-input" type="file" accept="image/*" className="hidden" onChange={handleAvatarPick} data-testid="avatar-file-input" />
      <div className="p-3 mx-3 mb-3 rounded-xl bg-white/[0.03] border border-white/[0.05]">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger"
              className="flex items-center gap-3 w-full p-1 rounded-lg hover:bg-white/[0.04] transition-all duration-200">
              <UserAvatar userId={user?.id} name={user?.name} className="h-9 w-9 text-xs ring-2 ring-[#6dbf4a]/30" testId="user-menu-avatar" />
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-[10px] text-white/30">{getRoleLabel(user?.role)}</p>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-white/20" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-[#12161e] border-white/10 rounded-xl shadow-2xl">
            <DropdownMenuLabel className="text-white/40 text-xs">Mitt konto</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/5" />
            <DropdownMenuItem className="gap-2 text-white/50 text-xs rounded-lg">
              <User className="w-3.5 h-3.5" /><span>{user?.email}</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 text-white/50 text-xs rounded-lg cursor-pointer" onClick={() => document.getElementById("avatar-file-input")?.click()} data-testid="change-avatar-btn">
              <Camera className="w-3.5 h-3.5" /><span>Byt profilbild</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 text-white/50 text-xs rounded-lg cursor-pointer" onClick={() => setPwOpen(true)} data-testid="change-password-btn">
              <KeyRound className="w-3.5 h-3.5" /><span>Byt losenord</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/5" />
            <DropdownMenuItem data-testid="logout-btn" className="gap-2 text-red-400 focus:text-red-400 rounded-lg" onClick={handleLogout}>
              <LogOut className="w-3.5 h-3.5" /><span>Logga ut</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#060a10' }}>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-60 lg:flex-col" style={{ background: 'linear-gradient(180deg, rgba(12,16,22,0.98) 0%, rgba(6,10,16,0.99) 100%)', borderRight: '1px solid rgba(255,255,255,0.03)' }}>
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 z-50 flex items-center justify-between px-4" style={{ background: 'rgba(6,10,16,0.9)', backdropFilter: 'blur(20px) saturate(180%)', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
        <img src={LOGO_DISPLAY} alt="Mälair" className="h-8 w-auto" style={{ mixBlendMode: 'screen', filter: 'brightness(1.2)' }} />
        <div className="flex items-center gap-1">
          {canManage && notifications.length > 0 && (
            <button onClick={() => setNotifOpen(!notifOpen)} className="relative p-2 text-white/40 hover:text-white transition-colors" data-testid="mobile-notif-btn">
              <Bell className="w-5 h-5" />
              <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-[#6dbf4a] text-[9px] text-white flex items-center justify-center font-bold shadow-lg shadow-[#6dbf4a]/30">{notifications.length}</span>
            </button>
          )}
          <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/50 hover:text-white hover:bg-white/[0.05]">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Overlay */}
      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setSidebarOpen(false)} />}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed inset-y-0 left-0 w-64 z-50 transform transition-transform duration-300 flex flex-col ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`} style={{ background: 'linear-gradient(180deg, #0d1117 0%, #080c12 100%)' }}>
        <SidebarContent onLinkClick={() => setSidebarOpen(false)} />
      </aside>

      {/* Desktop Notification Bell */}
      {canManage && notifications.length > 0 && (
        <div className="hidden lg:block fixed top-4 right-6 z-30">
          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <button className="relative p-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-white/40 hover:text-white hover:bg-white/[0.06] transition-all duration-200 backdrop-blur-lg" data-testid="notif-bell">
                <Bell className="w-4 h-4" />
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#6dbf4a] text-[10px] text-white flex items-center justify-center font-bold shadow-lg shadow-[#6dbf4a]/30 animate-pulse">{notifications.length}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 bg-[#12161e] border-white/10 rounded-xl p-0 shadow-2xl">
              <div className="px-4 py-3 border-b border-white/5">
                <p className="text-sm font-semibold text-white">Notifikationer</p>
              </div>
              {notifications.map(n => {
                const Icon = n.icon;
                return (
                  <button key={n.id} onClick={() => { navigate(n.path); setNotifOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.04] transition-all duration-200 text-left">
                    <Icon className={`w-4 h-4 flex-shrink-0 ${n.color}`} />
                    <span className="text-sm text-white/70 flex-1">{n.text}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-white/20" />
                  </button>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Main */}
      <main className="lg:pl-60 pt-14 lg:pt-0">
        <div className="min-h-[calc(100vh-3rem)] p-4 sm:p-6 lg:p-8">{children}</div>
        <footer className="px-6 py-4 border-t border-white/[0.03]">
          <p className="text-[11px] text-white/15 text-center tracking-wide">
            Malair Installation AB
          </p>
        </footer>
      </main>

      {/* Password Change Dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="bg-[#0d1218] border-white/[0.06] max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#6dbf4a]" /> Byt losenord
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="password" placeholder="Nuvarande losenord" value={pwForm.current}
              onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
              className="bg-[#080c12] border-white/[0.06] text-white" data-testid="pw-current" />
            <Input type="password" placeholder="Nytt losenord" value={pwForm.new1}
              onChange={e => setPwForm(p => ({ ...p, new1: e.target.value }))}
              className="bg-[#080c12] border-white/[0.06] text-white" data-testid="pw-new" />
            <Input type="password" placeholder="Bekrafta nytt losenord" value={pwForm.new2}
              onChange={e => setPwForm(p => ({ ...p, new2: e.target.value }))}
              className="bg-[#080c12] border-white/[0.06] text-white" data-testid="pw-confirm" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} className="border-white/[0.06] text-white/40">Avbryt</Button>
            <Button onClick={handleChangePassword} disabled={pwLoading || !pwForm.current || !pwForm.new1}
              className="bg-[#6dbf4a] hover:bg-[#5da83d]" data-testid="pw-submit">
              {pwLoading ? "Sparar..." : "Byt losenord"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
